// Lib
import React from 'react';
import classNames from 'classnames';
import { Crop02 } from '@untitledui/icons';

// Components
import Button from '../../../../../components/buttons/Button';
import Icon from '../../../../../components/icons/Icon';
import ImageModalBackgroundRemovalButton from './ImageModalBackgroundRemovalButton';

// Hooks
import { useTranslator } from '../../../../../components/i18n/useTranslator';

// Utils
import { getElementId } from '../../../../../../common/elements/utils/elementPropertyUtils';

// Types
import type { ImMNElement, MNImageContent } from '../../../../../../common/elements/elementModelTypes';
import type { BackgroundRemovalProgress } from './imageModalBackgroundRemovalProgressTypes';

// Styles
import './ImageModalV2EditorToolbar.scss';

type ImageModalV2EditorToolbarProps = {
    element: ImMNElement;
    cropSourceImage: MNImageContent;
    largestImageUrl?: string;
    imageType: string;
    cropActive: boolean;
    progress: BackgroundRemovalProgress | null;
    setProgress: (progress: BackgroundRemovalProgress | null) => void;
    disabled: boolean;
    onCropClick: () => void;
    onRotateClick: () => void;
    onCancelClick: () => void;
    onSaveClick: () => void;
};

const ImageModalV2EditorToolbar: React.FC<ImageModalV2EditorToolbarProps> = ({
    element,
    cropSourceImage,
    largestImageUrl,
    imageType,
    cropActive,
    progress,
    setProgress,
    disabled,
    onCropClick,
    onRotateClick,
    onCancelClick,
    onSaveClick,
}) => {
    const translate = useTranslator('popup', 'imageModal.editor');
    const elementId = getElementId(element);
    const cropButtonClassName = classNames('ImageModalV2EditorToolbar-tool-button', {
        active: cropActive,
    });

    return (
        <>
            <button
                type="button"
                className={cropButtonClassName}
                onClick={onCropClick}
                disabled={disabled}
                title="Crop"
            >
                <Crop02 size={24} />
            </button>

            <Button
                className="StyledButton secondary image-control rotate"
                onClickFn={onRotateClick}
                disabled={disabled}
            >
                <Icon name="modal-toolbar-rotate" />
                {translate('rotateLeft')}
            </Button>

            {largestImageUrl && (
                <ImageModalBackgroundRemovalButton
                    element={element}
                    elementId={elementId}
                    cropSourceImage={cropSourceImage}
                    largestImageUrl={largestImageUrl}
                    imageType={imageType}
                    progress={progress}
                    setProgress={setProgress}
                    disabled={disabled}
                />
            )}

            <Button className="StyledButton secondary cancel no-hover" onClickFn={onCancelClick}>
                {translate('cancel')}
            </Button>

            <Button className="StyledButton primary save" onClickFn={onSaveClick} disabled={disabled}>
                {translate('save')}
            </Button>
        </>
    );
};

export default ImageModalV2EditorToolbar;
